export const normalizeUserId = (value) => {
  const parsedId = Number(value)

  if (!Number.isInteger(parsedId) || parsedId <= 0) {
    return null
  }

  return parsedId
}

export const resolveTaskTemplateLaunchAssignee = (taskTemplate, fallbackUser = null) => {
  const assignedUserId = normalizeUserId(taskTemplate?.assigned_to_user_id ?? taskTemplate?.cached?.assigned_to_user?.id)
  const cachedName = typeof taskTemplate?.cached?.assigned_to_user?.name === 'string'
    ? taskTemplate.cached.assigned_to_user.name.trim()
    : ''
  const fallbackUserId = normalizeUserId(fallbackUser?.id)
  const fallbackUserName = typeof fallbackUser?.name === 'string' ? fallbackUser.name.trim() : ''

  if (cachedName !== '') {
    return {
      id: assignedUserId,
      name: cachedName,
      label: cachedName,
    }
  }

  if (assignedUserId !== null && fallbackUserId === assignedUserId && fallbackUserName !== '') {
    return {
      id: assignedUserId,
      name: fallbackUserName,
      label: fallbackUserName,
    }
  }

  if (assignedUserId !== null) {
    return {
      id: assignedUserId,
      name: null,
      label: `User #${assignedUserId}`,
    }
  }

  return {
    id: null,
    name: null,
    label: 'Unassigned',
  }
}

export const buildLaunchTargetOptions = ({ taskTemplate, currentUser } = {}) => {
  const defaultAssignee = resolveTaskTemplateLaunchAssignee(taskTemplate, currentUser)
  const currentUserId = normalizeUserId(currentUser?.id)

  return [
    {
      key: 'default',
      label: defaultAssignee.label,
      userId: defaultAssignee.id,
    },
    ...(currentUserId !== null && currentUserId !== defaultAssignee.id
      ? [{
          key: 'myself',
          label: 'Myself',
          userId: currentUserId,
        }]
      : []),
    {
      key: 'other-user',
      label: 'Other user',
      userId: null,
    },
  ]
}

export const resolvePrimaryLaunchMode = (defaultAssigneeId) => {
  return normalizeUserId(defaultAssigneeId) !== null ? 'default' : 'other-user'
}

export const buildTaskShowRoute = (taskId) => {
  const normalizedTaskId = normalizeUserId(taskId)

  if (normalizedTaskId === null) {
    return null
  }

  return {
    name: 'TaskShowPage',
    params: {
      task: normalizedTaskId,
    },
  }
}

export const buildLaunchSuccessMessage = (task, targetLabel = null) => {
  const taskId = normalizeUserId(task?.id)
  const messagePrefix = taskId !== null
    ? `Task #${taskId} created`
    : 'Task created'

  if (targetLabel === 'Unassigned') {
    return `${messagePrefix} without an assignee.`
  }

  if (targetLabel === 'you') {
    return `${messagePrefix} for you.`
  }

  if (typeof targetLabel === 'string' && targetLabel.trim() !== '') {
    return `${messagePrefix} for ${targetLabel}.`
  }

  return `${messagePrefix} from template.`
}

export const buildLaunchSuccessToastOptions = ({ taskRoute, navigate } = {}) => {
  const toastOptions = {
    duration: 20000,
    cancel: {
      label: 'Dismiss',
    },
  }

  if (taskRoute !== null && taskRoute !== undefined && typeof navigate === 'function') {
    toastOptions.action = {
      label: 'Open task',
      onClick: async () => navigate(taskRoute),
    }
  }

  return toastOptions
}

export const toDateTimeLocalValue = (dateValue) => {
  if (!dateValue) {
    return ''
  }

  const parsedDate = new Date(dateValue)

  if (Number.isNaN(parsedDate.getTime())) {
    return ''
  }

  const year = parsedDate.getFullYear()
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0')
  const day = String(parsedDate.getDate()).padStart(2, '0')
  const hours = String(parsedDate.getHours()).padStart(2, '0')
  const minutes = String(parsedDate.getMinutes()).padStart(2, '0')

  return `${year}-${month}-${day}T${hours}:${minutes}`
}

export const buildLaunchTaskTemplateDeadlineValue = (defaultDeadlineAfterMinutes, now = new Date()) => {
  const normalizedMinutes = Number(defaultDeadlineAfterMinutes)

  if (!Number.isInteger(normalizedMinutes) || normalizedMinutes < 1) {
    return ''
  }

  return toDateTimeLocalValue(new Date(now.getTime() + (normalizedMinutes * 60 * 1000)))
}

export const formatLaunchTaskTemplateDeadline = (deadlineAt) => {
  const parsedDeadline = new Date(deadlineAt)

  if (Number.isNaN(parsedDeadline.getTime())) {
    return null
  }

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsedDeadline)
}
